<template>
  <AiEducationLayout :menu-items="menuItems">
    <EducationPageHeader title="我的资料" description="查看并编辑当前登录账号的基本信息" />
    <ContentWrap>
      <EducationState v-if="loading" state="loading" title="正在加载资料" />
      <el-form
        v-else
        ref="formRef"
        :model="form"
        :rules="rules"
        label-width="96px"
        class="profile-form"
      >
        <el-form-item label="用户名">
          <el-input :model-value="profile?.username" disabled />
        </el-form-item>
        <el-form-item label="昵称" prop="nickname">
          <el-input v-model="form.nickname" maxlength="30" show-word-limit placeholder="请输入昵称" />
        </el-form-item>
        <el-form-item label="手机号" prop="mobile">
          <el-input v-model="form.mobile" maxlength="11" placeholder="请输入手机号" />
        </el-form-item>
        <el-form-item label="邮箱" prop="email">
          <el-input v-model="form.email" maxlength="50" placeholder="请输入邮箱" />
        </el-form-item>
        <el-form-item label="性别" prop="sex">
          <el-radio-group v-model="form.sex">
            <el-radio :value="1">男</el-radio>
            <el-radio :value="2">女</el-radio>
            <el-radio :value="0">未知</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" :loading="saving" @click="save">保存修改</el-button>
          <el-button :loading="loading" @click="load">重新加载</el-button>
        </el-form-item>
      </el-form>
    </ContentWrap>
  </AiEducationLayout>
</template>

<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue'
import type { FormInstance, FormRules } from 'element-plus'
import { ElMessage } from 'element-plus'
import AiEducationLayout from '../layout/AiEducationLayout.vue'
import type { EducationMenuItem } from '../layout/types'
import EducationPageHeader from '../components/EducationPageHeader.vue'
import EducationState from '../components/EducationState.vue'
import {
  getUserProfile,
  updateUserProfile,
  type ProfileVO,
  type UserProfileUpdateReqVO
} from '@/api/system/user/profile'

defineOptions({ name: 'AiEducationStudentProfile' })

const menuItems: EducationMenuItem[] = [
  {
    title: '学习首页',
    path: '/ai-Application-center/ai-education/student/home',
    icon: 'ep:home-filled'
  },
  {
    title: '我的课程',
    path: '/ai-Application-center/ai-education/student/courses',
    icon: 'ep:reading'
  },
  {
    title: '我的作业',
    path: '/ai-Application-center/ai-education/student/assignments',
    icon: 'ep:document-checked'
  },
  {
    title: 'AI 学伴',
    path: '/ai-Application-center/ai-education/student/assistant',
    icon: 'ep:magic-stick'
  }
]
const loading = ref(true)
const saving = ref(false)
const profile = ref<ProfileVO>()
const formRef = ref<FormInstance>()
const form = reactive<UserProfileUpdateReqVO>({
  nickname: '',
  mobile: '',
  email: '',
  sex: 0
})

const rules: FormRules = {
  nickname: [{ required: true, message: '请输入昵称', trigger: 'blur' }],
  mobile: [
    {
      pattern: /^1\d{10}$/,
      message: '手机号格式不正确',
      trigger: 'blur'
    }
  ],
  email: [{ type: 'email', message: '邮箱格式不正确', trigger: 'blur' }]
}

const load = async () => {
  loading.value = true
  try {
    profile.value = await getUserProfile()
    form.nickname = profile.value?.nickname || ''
    form.mobile = profile.value?.mobile || ''
    form.email = profile.value?.email || ''
    form.sex = profile.value?.sex ?? 0
  } catch (e: any) {
    ElMessage.error(e?.msg || e?.message || '资料加载失败')
  } finally {
    loading.value = false
  }
}

const save = async () => {
  const ok = await formRef.value?.validate().catch(() => false)
  if (!ok) return
  saving.value = true
  try {
    await updateUserProfile({
      nickname: form.nickname?.trim(),
      mobile: form.mobile?.trim(),
      email: form.email?.trim(),
      sex: form.sex
    })
    ElMessage.success('资料已保存')
    await load()
  } catch (e: any) {
    ElMessage.error(e?.msg || e?.message || '保存失败')
  } finally {
    saving.value = false
  }
}

onMounted(load)
</script>

<style scoped>
.profile-form {
  max-width: 520px;
}
</style>
